import React, { useState, useEffect } from 'react';
import { Database, Activity, RefreshCw, CheckCircle, XCircle, Power, Play, Info, Settings, Copy, Eye, EyeOff, BarChart3, Shield, Users, AlertCircle, Check, AlertTriangle, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FreeRADIUSMonitoring = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total_users: 0,
    active_sessions: 0,
    total_accounting: 0,
    database_size: 0
  });
  const [radiusInfo, setRadiusInfo] = useState({
    url: '',
    username: '',
    password: '',
    database_host: '',
    database_name: '',
    uptime_seconds: 0
  });
  const [showPassword, setShowPassword] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [healthStatus, setHealthStatus] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncStats, setSyncStats] = useState(null);
  
  // RADIUS Users state with server-side pagination
  const [radiusUsers, setRadiusUsers] = useState([]);
  const [usersCurrentPage, setUsersCurrentPage] = useState(1);
  const [usersItemsPerPage] = useState(10);
  const [usersTotalCount, setUsersTotalCount] = useState(0);
  const [usersTotalPages, setUsersTotalPages] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUsersSection, setShowUsersSection] = useState(false);
  const [settingStaticIp, setSettingStaticIp] = useState(null); // username being edited
  const [staticIpInput, setStaticIpInput] = useState('');

  useEffect(() => {
    fetchRadiusInfo();
    fetchRadiusStats();
  }, []);

  // Fetch RADIUS users with server-side pagination
  const fetchRadiusUsers = async (page = usersCurrentPage, search = usersSearch) => {
    setUsersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: usersItemsPerPage.toString()
      });
      if (search) {
        params.append('search', search);
      }
      
      const response = await axios.get(`${API_URL}/api/radius/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setRadiusUsers(response.data.data || []);
        const pagination = response.data.pagination;
        if (pagination) {
          setUsersTotalCount(pagination.total_count);
          setUsersTotalPages(pagination.total_pages);
        }
        setShowUsersSection(true);
      }
    } catch (error) {
      console.error('Error fetching RADIUS users:', error);
      toast.error('Gagal mengambil data RADIUS users');
    } finally {
      setUsersLoading(false);
    }
  };
  
  // Handle page change
  const handleUsersPageChange = (newPage) => {
    setUsersCurrentPage(newPage);
    fetchRadiusUsers(newPage, usersSearch);
  };
  
  // Handle search
  const handleUsersSearch = () => {
    setUsersCurrentPage(1);
    fetchRadiusUsers(1, usersSearch);
  };

  // Delete RADIUS user
  const deleteRadiusUser = async (username) => {
    if (!window.confirm(`Hapus user "${username}" dari RADIUS?\n\nUser tidak akan bisa login PPPoE!`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/api/radius/users/${username}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchRadiusUsers(); // Refresh list
        fetchRadiusStats(); // Refresh stats
      } else {
        toast.error(response.data.message || 'Gagal menghapus user');
      }
    } catch (error) {
      console.error('Error deleting RADIUS user:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus user');
    }
  };

  // Set static IP for RADIUS user
  const setStaticIpForUser = async (username) => {
    if (!staticIpInput || !staticIpInput.match(/^(\d{1,3}\.){3}\d{1,3}$/)) {
      toast.error('Format IP address tidak valid');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/radius/users/${username}/set-static-ip`,
        { ip_address: staticIpInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        setSettingStaticIp(null);
        setStaticIpInput('');
        fetchRadiusUsers(); // Refresh list
      } else {
        toast.error(response.data.message || 'Gagal set static IP');
      }
    } catch (error) {
      console.error('Error setting static IP:', error);
      toast.error(error.response?.data?.detail || 'Gagal set static IP');
    }
  };

  const fetchRadiusInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/radius/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setRadiusInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching RADIUS info:', error);
      // Don't show toast for info fetch failures
    }
  };

  const fetchRadiusStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/radius/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching RADIUS stats:', error);
      // Only show error if it's not a permission issue (403)
      if (error.response?.status !== 403) {
        toast.error('Gagal memuat statistik RADIUS');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRadiusAction = async (action) => {
    const actionNames = {
      reboot: 'Reboot',
      start: 'Start',
      stop: 'Stop',
      restart: 'Restart'
    };

    const confirmed = window.confirm(
      `Apakah Anda yakin ingin ${actionNames[action]} FreeRADIUS Server?\n\nIni akan mempengaruhi autentikasi semua user!`
    );

    if (!confirmed) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/radius/control/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`FreeRADIUS Server ${actionNames[action]} berhasil`);
        // Refresh stats after action
        setTimeout(() => fetchRadiusStats(), 2000);
      }
    } catch (error) {
      console.error(`Error ${action} RADIUS:`, error);
      toast.error(error.response?.data?.detail || `Gagal ${actionNames[action]} FreeRADIUS Server`);
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} berhasil disalin`);
    }).catch(() => {
      toast.error('Gagal menyalin');
    });
  };

  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const runHealthCheck = async () => {
    setHealthLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/radius/health-check`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000 // 90 seconds timeout for health check
      });

      if (response.data.success) {
        setHealthStatus(response.data.data);
        const status = response.data.data.overall_status;
        if (status === 'healthy') {
          toast.success('Health check completed - All systems operational');
        } else if (status === 'warning') {
          toast.warning('Health check completed - Some warnings detected');
        } else {
          toast.error('Health check completed - Critical issues detected');
        }
      }
    } catch (error) {
      console.error('Error running health check:', error);
      toast.error(error.response?.data?.detail || 'Gagal menjalankan health check');
    } finally {
      setHealthLoading(false);
    }
  };

  const syncUsersToRADIUS = async () => {
    setSyncLoading(true);
    toast.info('Memulai sync... Proses ini mungkin memakan waktu beberapa menit.');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/radius/sync-users-mysql?remove_stale=true`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000 // 5 minutes timeout for user sync
        }
      );

      if (response.data.success) {
        const stats = response.data.data?.stats || response.data.data || {};
        setSyncStats(stats);
        toast.success(`Sync selesai! Success: ${stats.success || 0}, Failed: ${stats.failed || 0}`);
        // Refresh stats and users
        fetchRadiusStats();
        fetchRadiusUsers(1, usersSearch);
      } else {
        toast.error(response.data.message || 'Sync failed');
      }
    } catch (error) {
      console.error('Error syncing users:', error);
      if (error.code === 'ECONNABORTED') {
        toast.warning('Sync timeout - proses masih berjalan di background. Refresh halaman untuk lihat hasil.');
      } else {
        toast.error(error.response?.data?.detail || 'Gagal sync users ke RADIUS');
      }
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 space-y-4">
      {/* Header - Compact */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center space-x-2">
              <Database className="w-6 h-6 text-green-600 dark:text-green-400" />
              <span>FreeRADIUS Monitoring</span>
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Authentication & Accounting Management Console
            </p>
          </div>
          {radiusInfo.uptime_seconds > 0 && (
            <div className="px-3 py-1.5 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center space-x-2">
                <Activity className="w-4 h-4 text-green-600 dark:text-green-400 animate-pulse" />
                <div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">Uptime</div>
                  <div className="text-sm font-semibold text-green-700 dark:text-green-300">{formatUptime(radiusInfo.uptime_seconds)}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        <Button
          onClick={fetchRadiusStats}
          disabled={loading}
          size="sm"
          className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
        >
          <RefreshCw className={`w-3 h-3 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* RADIUS Server Information & Controls - Compact */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* RADIUS Server Info */}
        <Card className="border border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-base">
              <Info className="w-4 h-4 text-green-600 dark:text-green-400" />
              <span>Server Information</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Database Host</label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(radiusInfo.database_host, 'Database Host')}
                  className="h-6 px-2"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <Input
                value={radiusInfo.database_host || 'Loading...'}
                readOnly
                className="h-8 bg-slate-50 dark:bg-slate-900 font-mono text-xs border-slate-200 dark:border-slate-700"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Database Name</label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(radiusInfo.database_name, 'Database Name')}
                  className="h-6 px-2"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <Input
                value={radiusInfo.database_name || 'Loading...'}
                readOnly
                className="h-8 bg-slate-50 dark:bg-slate-900 font-mono text-xs border-slate-200 dark:border-slate-700"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Username</label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(radiusInfo.username, 'Username')}
                  className="h-6 px-2"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <Input
                value={radiusInfo.username || 'Loading...'}
                readOnly
                className="h-8 bg-slate-50 dark:bg-slate-900 font-mono text-xs border-slate-200 dark:border-slate-700"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Password</label>
                <div className="flex items-center space-x-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowPassword(!showPassword)}
                    className="h-6 px-2"
                  >
                    {showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(radiusInfo.password, 'Password')}
                    className="h-6 px-2"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={radiusInfo.password || 'Loading...'}
                readOnly
                className="h-8 bg-slate-50 dark:bg-slate-900 font-mono text-xs border-slate-200 dark:border-slate-700"
              />
            </div>

            <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                💡 Bagikan kredensial ini ke tenant untuk akses FreeRADIUS
              </p>
            </div>
          </CardContent>
        </Card>

        {/* RADIUS Server Controls */}
        <Card className="border border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-base">
              <Settings className="w-4 h-4 text-green-600 dark:text-green-400" />
              <span>Server Control</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => handleRadiusAction('start')}
                disabled={actionLoading}
                size="sm"
                className="bg-green-500 hover:bg-green-600 text-white h-9"
              >
                <Play className="w-3 h-3 mr-1.5" />
                Start
              </Button>

              <Button
                onClick={() => handleRadiusAction('restart')}
                disabled={actionLoading}
                size="sm"
                className="bg-blue-500 hover:bg-blue-600 text-white h-9"
              >
                <RefreshCw className="w-3 h-3 mr-1.5" />
                Restart
              </Button>

              <Button
                onClick={() => handleRadiusAction('reboot')}
                disabled={actionLoading}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white h-9"
              >
                <Power className="w-3 h-3 mr-1.5" />
                Reboot
              </Button>

              <Button
                onClick={() => handleRadiusAction('stop')}
                disabled={actionLoading}
                size="sm"
                className="bg-red-500 hover:bg-red-600 text-white h-9"
              >
                <XCircle className="w-3 h-3 mr-1.5" />
                Stop
              </Button>
            </div>

            <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-red-500 dark:text-red-400">
                ⚠️ Actions affect all user authentication
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards - Compact */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card className="border border-green-200 dark:border-green-800 bg-gradient-to-br from-green-50 to-white dark:from-green-900/20 dark:to-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-green-600 dark:text-green-400">TOTAL USERS</p>
                <p className="text-2xl font-bold text-green-700 dark:text-green-300 mt-1">{stats.total_users}</p>
              </div>
              <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-lg">
                <Database className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400">ACTIVE SESSIONS</p>
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-300 mt-1">{stats.active_sessions}</p>
              </div>
              <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-purple-600 dark:text-purple-400">ACCOUNTING RECORDS</p>
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-300 mt-1">{stats.total_accounting}</p>
              </div>
              <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
                <BarChart3 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-indigo-200 dark:border-indigo-800 bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-900/20 dark:to-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-indigo-600 dark:text-indigo-400">DB SIZE</p>
                <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-300 mt-1">{stats.database_size} MB</p>
              </div>
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg">
                <Database className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Health Check & User Sync Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Health Check Card */}
        <Card className="border border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-base">
              <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <span>RADIUS Health Check</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Monitor kesehatan FreeRADIUS server
              </p>
              <Button
                onClick={runHealthCheck}
                disabled={healthLoading}
                size="sm"
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Shield className={`w-3 h-3 mr-1.5 ${healthLoading ? 'animate-spin' : ''}`} />
                {healthLoading ? 'Checking...' : 'Run Check'}
              </Button>
            </div>

            {healthStatus && (
              <div className="space-y-3 mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                {/* Overall Status Badge */}
                <div className={`p-4 rounded-lg ${
                  healthStatus.overall_status === 'healthy' 
                    ? 'bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-900/10 border-2 border-green-300 dark:border-green-700' 
                    : healthStatus.overall_status === 'warning'
                    ? 'bg-gradient-to-r from-yellow-50 to-yellow-100 dark:from-yellow-900/30 dark:to-yellow-900/10 border-2 border-yellow-300 dark:border-yellow-700'
                    : 'bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-900/10 border-2 border-red-300 dark:border-red-700'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {healthStatus.overall_status === 'healthy' ? (
                        <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                      ) : healthStatus.overall_status === 'warning' ? (
                        <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                      )}
                      <div>
                        <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">Overall Status</p>
                        <p className="text-lg font-bold text-slate-900 dark:text-slate-100">
                          {healthStatus.overall_status.toUpperCase()}
                        </p>
                      </div>
                    </div>
                    <Shield className="w-8 h-8 text-slate-300 dark:text-slate-600" />
                  </div>
                </div>

                {/* Check Results Grid */}
                <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto pr-1">
                  {healthStatus.checks && healthStatus.checks.map((check, idx) => {
                    const isPass = check.includes('✅ PASS');
                    const isWarning = check.includes('⚠️ WARNING');
                    const isFail = check.includes('❌ FAIL') || check.includes('❌ CRITICAL');
                    
                    return (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border-l-4 ${
                          isPass 
                            ? 'bg-green-50/50 dark:bg-green-900/10 border-green-500' 
                            : isWarning
                            ? 'bg-yellow-50/50 dark:bg-yellow-900/10 border-yellow-500'
                            : 'bg-red-50/50 dark:bg-red-900/10 border-red-500'
                        }`}
                      >
                        <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
                          {check}
                        </p>
                      </div>
                    );
                  })}
                </div>

                {/* Timestamp */}
                <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-700">
                  <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center space-x-1">
                    <Activity className="w-3 h-3" />
                    <span>Last checked: {new Date(healthStatus.timestamp).toLocaleString('id-ID')}</span>
                  </p>
                </div>
              </div>
            )}

            {!healthStatus && (
              <div className="text-center py-6 text-sm text-slate-500 dark:text-slate-400">
                Klik "Run Check" untuk memulai health monitoring
              </div>
            )}
          </CardContent>
        </Card>

        {/* User Sync Card */}
        <Card className="border border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-base">
              <Users className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <span>User Auto-Sync</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Sync users MongoDB → RADIUS MySQL
              </p>
              <Button
                onClick={syncUsersToRADIUS}
                disabled={syncLoading}
                size="sm"
                className="bg-purple-500 hover:bg-purple-600 text-white"
              >
                <RefreshCw className={`w-3 h-3 mr-1.5 ${syncLoading ? 'animate-spin' : ''}`} />
                {syncLoading ? 'Syncing...' : 'Sync Now'}
              </Button>
            </div>

            {syncStats && (
              <div className="space-y-2 mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <div>
                        <p className="text-xs text-slate-600 dark:text-slate-400">New Users</p>
                        <p className="text-lg font-bold text-green-700 dark:text-green-300">{syncStats.synced}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <RefreshCw className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <div>
                        <p className="text-xs text-slate-600 dark:text-slate-400">Updated</p>
                        <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{syncStats.updated}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                      <div>
                        <p className="text-xs text-slate-600 dark:text-slate-400">Removed</p>
                        <p className="text-lg font-bold text-red-700 dark:text-red-300">{syncStats.deleted}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                      <div>
                        <p className="text-xs text-slate-600 dark:text-slate-400">Errors</p>
                        <p className="text-lg font-bold text-orange-700 dark:text-orange-300">{syncStats.errors}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!syncStats && (
              <div className="text-center py-6 text-sm text-slate-500 dark:text-slate-400">
                Klik "Sync Now" untuk sinkronisasi users
              </div>
            )}

            <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                💡 Sync otomatis menambah, update, dan hapus users
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* RADIUS Users List */}
      <Card className="border border-slate-200 dark:border-slate-700">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              RADIUS Users Database
              {usersTotalCount > 0 && (
                <span className="text-xs font-normal text-slate-500 dark:text-slate-400">({usersTotalCount} total)</span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Input
                  placeholder="Cari username..."
                  value={usersSearch}
                  onChange={(e) => setUsersSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleUsersSearch()}
                  className="h-8 w-40 text-sm"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleUsersSearch}
                  className="h-8 px-2"
                >
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              <Button
                size="sm"
                onClick={() => fetchRadiusUsers(1, usersSearch)}
                disabled={usersLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${usersLoading ? 'animate-spin' : ''}`} />
                {usersLoading ? 'Loading...' : 'Load Users'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!showUsersSection ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Klik "Load Users" untuk melihat daftar user RADIUS</p>
            </div>
          ) : radiusUsers.length === 0 ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{usersSearch ? 'Tidak ada user yang cocok dengan pencarian' : 'Tidak ada user di database RADIUS'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-slate-500 mb-2 dark:text-slate-400">
                Menampilkan {((usersCurrentPage - 1) * usersItemsPerPage) + 1} - {Math.min(usersCurrentPage * usersItemsPerPage, usersTotalCount)} dari {usersTotalCount} users
              </div>
              {radiusUsers.map((user, index) => (
                <div 
                  key={index}
                  className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-500" />
                      <span className="font-mono text-sm font-semibold">{user.username}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSettingStaticIp(user.username);
                          setStaticIpInput(user.attributes?.['Framed-IP-Address'] || '');
                        }}
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 h-7 px-2 dark:hover:bg-blue-900/30"
                        title="Set Static IP"
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteRadiusUser(user.username)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2 dark:hover:bg-red-900/30"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Static IP Form */}
                  {settingStaticIp === user.username && (
                    <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="172.16.10.100"
                          value={staticIpInput}
                          onChange={(e) => setStaticIpInput(e.target.value)}
                          className="h-8 text-sm"
                        />
                        <Button
                          size="sm"
                          onClick={() => setStaticIpForUser(user.username)}
                          className="bg-blue-600 hover:bg-blue-700 h-8"
                        >
                          Set IP
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSettingStaticIp(null);
                            setStaticIpInput('');
                          }}
                          className="h-8"
                        >
                          Batal
                        </Button>
                      </div>
                      <p className="text-xs text-blue-600 mt-1 dark:text-blue-400">
                        💡 Set IP statis untuk bypass Framed-Pool
                      </p>
                    </div>
                  )}
                  
                  {/* Attributes */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                    {user.attributes && Object.keys(user.attributes).length > 0 ? (
                      Object.entries(user.attributes).map(([attr, value]) => (
                        <div 
                          key={attr}
                          className={`px-2 py-1 rounded ${
                            attr === 'Framed-Pool' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                              : attr === 'Framed-IP-Address'
                              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                          }`}
                        >
                          <span className="font-medium">{attr}:</span> {value}
                        </div>
                      ))
                    ) : (
                      <div className="px-2 py-1 rounded bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 col-span-full">
                        ⚠️ Tidak ada attributes (Framed-Pool tidak ada!)
                      </div>
                    )}
                  </div>
                  
                  {/* Warning if no Framed-Pool and no Framed-IP-Address */}
                  {user.attributes && !user.attributes['Framed-Pool'] && !user.attributes['Framed-IP-Address'] && Object.keys(user.attributes).length > 0 && (
                    <div className="mt-2 px-2 py-1 rounded bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 text-xs">
                      ⚠️ Framed-Pool/Framed-IP-Address tidak ada! User tidak akan dapat IP. Klik ⚙️ untuk set IP statis.
                    </div>
                  )}
                </div>
              ))}
              
              {/* Pagination Controls */}
              {usersTotalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Halaman {usersCurrentPage} dari {usersTotalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUsersPageChange(Math.max(1, usersCurrentPage - 1))}
                      disabled={usersCurrentPage === 1 || usersLoading}
                      className="h-8 px-3"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Prev
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, usersTotalPages) }, (_, i) => {
                        let pageNum;
                        if (usersTotalPages <= 5) {
                          pageNum = i + 1;
                        } else if (usersCurrentPage <= 3) {
                          pageNum = i + 1;
                        } else if (usersCurrentPage >= usersTotalPages - 2) {
                          pageNum = usersTotalPages - 4 + i;
                        } else {
                          pageNum = usersCurrentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            size="sm"
                            variant={usersCurrentPage === pageNum ? "default" : "outline"}
                            onClick={() => handleUsersPageChange(pageNum)}
                            disabled={usersLoading}
                            className="h-8 w-8 p-0"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUsersPageChange(Math.min(usersTotalPages, usersCurrentPage + 1))}
                      disabled={usersCurrentPage === usersTotalPages || usersLoading}
                      className="h-8 px-3"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Database Tables Info */}
      <Card className="border border-slate-200 dark:border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">
            Database Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">radcheck</span>
                <CheckCircle className="w-4 h-4 text-green-500" />
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400">User authentication table</p>
            </div>

            <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">radacct</span>
                <CheckCircle className="w-4 h-4 text-green-500" />
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Accounting & session logs</p>
            </div>

            <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">radreply</span>
                <CheckCircle className="w-4 h-4 text-green-500" />
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400">User reply attributes</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FreeRADIUSMonitoring;
